/**
 * Phase 7 Commit J — Service step.
 *
 * Two layouts gated by `behavior.serviceMenuLayout`:
 *
 *   - 'classic' (default): Search input + horizontal category rail +
 *     sectioned service cards. Tap a card to highlight (App's RunningBar
 *     commits via Continue). Bestseller / new badges + optional booked-
 *     count proof line read from bootstrap fields (bookedLast30d,
 *     featured).
 *
 *   - 'tiered': Landing-page-style menu — category tabs at top, one
 *     card per base service ("Full Balayage" appears once, not three
 *     times), tier buttons on the right (e.g. MEDIUM $240 2h 30m /
 *     LONG $300 3h). Tapping a tier button selects that specific
 *     variant + advances to the next step. Designed for premium-tone-
 *     preset salons whose Square catalog uses the "<base> — <tier>"
 *     naming convention.
 */

import { useMemo, useState } from 'preact/hooks';
import type { BootstrapResponse } from '../types';
import { SearchIcon, StarIcon } from '../icons';

interface CategoryFilter {
  id: string;
  name: string;
  serviceIds: string[];
}

interface Props {
  bootstrap: BootstrapResponse;
  selectedId: string | null;
  onSelect: (serviceId: string) => void;
  categoryFilter: CategoryFilter | null;
  onClearCategory: () => void;
}

// `startingAt` renders a trailing "+" — the catalog price is a floor, not
// the final bill (extensions and colour scale with length/density). Purely a
// display concern: the deposit is still computed from the catalog price.
const formatPrice = (
  p: { amountCents: number; currency: string },
  startingAt = false,
): string =>
  '$' + (p.amountCents / 100).toLocaleString() + (startingAt ? '+' : '');

const formatDuration = (m: number): string => {
  // Empty string for 0/negative — operators leave durationMinutes=0
  // in their POS when they don't want to commit to a fixed time (KC
  // has ~40 of 118 services like this). The render sites skip the
  // duration span entirely when this returns '' so the UI doesn't
  // show a confusing "0m" / "+0m" badge.
  if (m <= 0) return '';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};

export function ServiceStep(props: Props) {
  // Branch on layout up front so the classic path stays byte-identical
  // to its pre-tiered behavior. Default to classic when unset.
  const layout = props.bootstrap.behavior?.serviceMenuLayout ?? 'classic';
  // M19 — keying on categoryFilter.id remounts TieredServiceStep when
  // the deep-link category changes, so the activeKey useState lazy
  // initializer re-runs with the new categoryFilter. Replaces a derived-
  // state useEffect that would have been an unnecessary extra render.
  if (layout === 'tiered')
    return (
      <TieredServiceStep
        key={props.categoryFilter?.id ?? 'default'}
        {...props}
      />
    );
  return <ClassicServiceStep {...props} />;
}

function ClassicServiceStep({
  bootstrap,
  selectedId,
  onSelect,
  categoryFilter,
  onClearCategory,
}: Props) {
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState<string | null>(null);

  const showPrices = bootstrap.behavior?.showPrices ?? true;
  const showDescriptions = bootstrap.behavior?.showDescriptions ?? false;
  const startingAt = bootstrap.behavior?.startingAtPrices ?? false;
  // Inline "from price" label ("Starting from $285"). Rendered smaller and
  // muted so the amount still reads as the price at a glance; the trailing
  // "+" is dropped when it's set (the words already say it).
  const priceLabel = bootstrap.priceLabelCopy ?? null;

  type ServiceList = BootstrapResponse['services'];
  type Section = {
    key: string;
    label: string;
    description: string | null;
    services: ServiceList;
  };

  // Defensive — bootstrap is the source of truth (drops empty groups
  // upstream), but if a future code path injects a 0-count section the
  // category rail would render a useless chip. Drop those here too.
  const baseSections: Section[] = useMemo(() => {
    const out: Section[] = [];
    // Exclude UNLISTED services from the browsable menu — they exist in
    // bootstrap.services only so a `?service=` deep link can preselect + book
    // them; they must never appear in a category or the "all services" list.
    const menuServices = bootstrap.services.filter((s) => !s.hidden);
    if (categoryFilter) {
      const byId = new Map(menuServices.map((s) => [s.id, s]));
      const filtered = categoryFilter.serviceIds
        .map((id) => byId.get(id))
        .filter((s): s is ServiceList[number] => !!s);
      out.push({
        key: '__filter__',
        label: categoryFilter.name,
        description: null,
        services: filtered,
      });
    } else if (bootstrap.serviceGroups && bootstrap.serviceGroups.length > 0) {
      for (const g of bootstrap.serviceGroups) {
        out.push({
          key: g.id,
          label: g.name,
          description: g.description,
          services: g.services,
        });
      }
      if (
        bootstrap.ungroupedServices &&
        bootstrap.ungroupedServices.length > 0
      ) {
        out.push({
          key: '__ungrouped__',
          label: 'Other services',
          description: null,
          services: bootstrap.ungroupedServices,
        });
      }
    } else {
      const byCategory = new Map<string, ServiceList>();
      for (const s of menuServices) {
        const cat = s.categoryName ?? 'Services';
        if (!byCategory.has(cat)) byCategory.set(cat, []);
        byCategory.get(cat)!.push(s);
      }
      for (const [label, services] of byCategory.entries()) {
        out.push({ key: label, label, description: null, services });
      }
    }
    return out.filter((s) => s.services.length > 0);
  }, [bootstrap, categoryFilter]);

  // Apply search + category-rail filters on top of base sections.
  const sections = useMemo(() => {
    let result = baseSections;
    if (activeCat) {
      result = result.filter((s) => s.label === activeCat);
    }
    const q = query.trim().toLowerCase();
    if (q) {
      result = result
        .map((s) => ({
          ...s,
          services: s.services.filter(
            (svc) =>
              svc.name.toLowerCase().includes(q) ||
              svc.description?.toLowerCase().includes(q),
          ),
        }))
        .filter((s) => s.services.length > 0);
    }
    return result;
  }, [baseSections, activeCat, query]);

  const totalCount = baseSections.reduce(
    (sum, s) => sum + s.services.length,
    0,
  );

  // Search earns its place on a long catalogue only. Below this the category
  // rail already does the filtering, and on a phone the input pushes the first
  // service card below the fold — the single most expensive pixel cost we have.
  const showSearch = totalCount >= 25 && !categoryFilter;
  const showCatRail =
    !categoryFilter && baseSections.length > 1 && baseSections.length <= 12;

  return (
    <div class="sbw-scroll">
      {categoryFilter && (
        <div class="sbw-category-filter">
          <span class="sbw-category-filter-label">
            Showing: <strong>{categoryFilter.name}</strong>
          </span>
          <button
            type="button"
            class="sbw-category-filter-clear"
            onClick={onClearCategory}
          >
            See all services
          </button>
        </div>
      )}

      {showSearch && (
        <div class="sbw-search">
          <span class="sbw-search-icon">
            <SearchIcon />
          </span>
          <input
            placeholder={`Search ${totalCount} services`}
            value={query}
            onInput={(e) =>
              setQuery((e.target as HTMLInputElement).value)
            }
          />
        </div>
      )}

      {showCatRail && (
        <div class="sbw-cat-rail">
          <button
            type="button"
            class={'sbw-cat' + (activeCat === null ? ' is-active' : '')}
            onClick={() => setActiveCat(null)}
          >
            All
            <span class="sbw-cat-count">{totalCount}</span>
          </button>
          {baseSections.map((s) => (
            <button
              key={s.key}
              type="button"
              class={'sbw-cat' + (activeCat === s.label ? ' is-active' : '')}
              onClick={() => setActiveCat(s.label)}
            >
              {s.label}
              <span class="sbw-cat-count">{s.services.length}</span>
            </button>
          ))}
        </div>
      )}

      {sections.map((section) => (
        <div key={section.key}>
          <div class="sbw-section-head">
            <h3>{section.label}</h3>
            <span class="sbw-section-meta">
              {section.services.length}{' '}
              {section.services.length === 1 ? 'option' : 'options'}
            </span>
          </div>
          {section.description && (
            <p class="sbw-category-desc">{section.description}</p>
          )}
          {section.services.map((s) => {
            const isSelected = selectedId === s.id;
            const isBestseller = (s.bookedLast30d ?? 0) >= 20;
            return (
              <div
                key={s.id}
                class={'sbw-svc' + (isSelected ? ' is-selected' : '')}
                onClick={() => onSelect(s.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(s.id);
                  }
                }}
              >
                <div>
                  {isBestseller && (
                    <div class="sbw-svc-badge">
                      <StarIcon size={9} /> Bestseller
                      {s.bookedLast30d && (
                        <span class="sbw-svc-proof">
                          · booked {s.bookedLast30d}× this month
                        </span>
                      )}
                    </div>
                  )}
                  <div class="sbw-svc-name">{s.name}</div>
                  {formatDuration(s.durationMinutes) && (
                    <div class="sbw-svc-meta">
                      <span>{formatDuration(s.durationMinutes)}</span>
                    </div>
                  )}
                  {showDescriptions && s.description && isSelected && (
                    <div class="sbw-svc-desc">{s.description}</div>
                  )}
                </div>
                {showPrices && (
                  <div class="sbw-svc-price">
                    {priceLabel && (
                      <span class="sbw-svc-price-label">{priceLabel} </span>
                    )}
                    {formatPrice(s.price, startingAt && !priceLabel)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {sections.length === 0 && (
        <div class="sbw-empty">No services match that search.</div>
      )}
    </div>
  );
}

// ============================================================================
// Tiered layout — landing-page-style menu for premium-preset salons.
// ============================================================================

const TIER_ORDER = ['Short', 'Medium', 'Long'];

function tierIndex(label: string): number {
  const i = TIER_ORDER.indexOf(label);
  return i >= 0 ? i : 99;
}

interface TieredVariant {
  id: string;
  tierLabel: string | null; // null = no tier suffix → render as single button
  price: { amountCents: number; currency: string };
  durationMinutes: number;
  bookedLast30d: number;
}

interface TieredService {
  baseName: string;
  description: string | null;
  variants: TieredVariant[];
  isMostBooked: boolean;
}

/**
 * Group a flat service list into one entry per base name. Detects the
 * " — TIER" suffix; services without that suffix become single-variant
 * entries. Output preserves the input ordering of base names so the
 * admin's per-group displayOrder still drives the card sequence.
 */
function groupByBaseName(
  services: BootstrapResponse['services'],
): TieredService[] {
  const order: string[] = [];
  const map = new Map<string, TieredService>();

  for (const s of services) {
    const dashIdx = s.name.indexOf(' — ');
    const baseName = dashIdx >= 0 ? s.name.slice(0, dashIdx) : s.name;
    const tierLabel = dashIdx >= 0 ? s.name.slice(dashIdx + 3).trim() : null;

    let entry = map.get(baseName);
    if (!entry) {
      entry = {
        baseName,
        // First variant's description seeds the card. Variants of the
        // same base service almost always share copy in practice; if
        // they don't, the first one wins (admin can edit downstream).
        description: s.description ?? null,
        variants: [],
        isMostBooked: false,
      };
      map.set(baseName, entry);
      order.push(baseName);
    }
    entry.variants.push({
      id: s.id,
      tierLabel,
      price: s.price,
      durationMinutes: s.durationMinutes,
      bookedLast30d: s.bookedLast30d ?? 0,
    });
    // Card-level "most booked" pill if any variant has hit the bestseller
    // threshold over the last 30 days. Same threshold as the classic
    // layout's bestseller badge for consistency.
    if ((s.bookedLast30d ?? 0) >= 20) {
      entry.isMostBooked = true;
    }
  }

  for (const e of map.values()) {
    e.variants.sort((a, b) => {
      const at = a.tierLabel ?? '';
      const bt = b.tierLabel ?? '';
      const ai = tierIndex(at);
      const bi = tierIndex(bt);
      if (ai !== bi) return ai - bi;
      // Stable for unknown tiers — order by price ascending.
      return a.price.amountCents - b.price.amountCents;
    });
  }

  return order.map((name) => map.get(name)!);
}

function TieredServiceStep({ bootstrap, selectedId, onSelect, categoryFilter }: Props) {
  const startingAt = bootstrap.behavior?.startingAtPrices ?? false;
  // For the tiered layout the categories ARE the navigation; sections come
  // straight from serviceGroups. Search works across every group — typing
  // a query flattens the active-tab restriction so the operator can find
  // a service without remembering which category it lives in.
  //
  // Deep linking: `?category=<group-id>` arrives via App.tsx → categoryFilter
  // prop. In tiered mode we don't filter the service list (the tabs already
  // are the filter); instead we seed the initial active tab so the
  // landing-page operator can deep link to e.g. `?category=<color-uuid>` and
  // have the widget open on Color. Falls back to the first group if the id
  // doesn't match any tab.
  //
  // Auto-derive groups from provider categoryNames when no custom
  // WidgetServiceGroups are configured (matches classic mode's fallback
  // at the `byCategory` branch above). Without this, tiered mode would
  // render an empty state for any tenant that hasn't manually built
  // custom groups — which is most tenants out of the box.
  const groups = useMemo<NonNullable<BootstrapResponse['serviceGroups']>>(() => {
    if (bootstrap.serviceGroups && bootstrap.serviceGroups.length > 0) {
      return bootstrap.serviceGroups;
    }
    const byCat = new Map<string, BootstrapResponse['services']>();
    for (const s of bootstrap.services) {
      const cat = s.categoryName ?? 'Services';
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat)!.push(s);
    }
    return Array.from(byCat.entries()).map(([name, services]) => ({
      id: `cat:${name}`,
      name,
      description: null,
      services,
    }));
  }, [bootstrap.serviceGroups, bootstrap.services]);
  const initialActiveKey = useMemo(() => {
    if (categoryFilter && groups.some((g) => g.id === categoryFilter.id)) {
      return categoryFilter.id;
    }
    return groups[0]?.id ?? '';
  }, [categoryFilter, groups]);
  const [activeKey, setActiveKey] = useState<string>(initialActiveKey);
  const [query, setQuery] = useState('');

  // M19 — the previous defensive useEffect that re-synced activeKey when
  // categoryFilter arrived after first paint has been replaced by a
  // `key={categoryFilter?.id ?? 'default'}` reset on the parent's
  // <TieredServiceStep> render. When categoryFilter.id changes the whole
  // step remounts, so the useState lazy initializer above picks up the
  // new value cleanly without an extra render cycle.

  const activeGroup = groups.find((g) => g.id === activeKey) ?? groups[0];
  const trimmedQuery = query.trim().toLowerCase();
  const isSearching = trimmedQuery.length > 0;

  // H3 — precompute groupByBaseName per group once (driven only by groups
  // identity). Without this, every keystroke in the search input runs
  // groupByBaseName for every group, allocating fresh Maps and arrays.
  const groupedByGroup = useMemo(
    () =>
      groups.map((g) => ({ id: g.id, items: groupByBaseName(g.services) })),
    [groups],
  );
  const groupedById = useMemo(
    () => new Map(groupedByGroup.map((g) => [g.id, g.items])),
    [groupedByGroup],
  );

  // Build the visible tiered list. Empty query → active category only.
  // Non-empty query → flatten every group, filter by name/description,
  // dedupe by base name (a service that lived in two groups would otherwise
  // render twice). The order preserves group displayOrder, then within-
  // group displayOrder.
  const visible = useMemo(() => {
    if (!isSearching) {
      return activeGroup ? groupedById.get(activeGroup.id) ?? [] : [];
    }
    const seen = new Set<string>();
    const flat: TieredService[] = [];
    for (const g of groups) {
      const items = groupedById.get(g.id) ?? [];
      for (const svc of items) {
        if (seen.has(svc.baseName)) continue;
        const hay = (svc.baseName + ' ' + (svc.description ?? '')).toLowerCase();
        if (!hay.includes(trimmedQuery)) continue;
        seen.add(svc.baseName);
        flat.push(svc);
      }
    }
    return flat;
  }, [activeGroup, groups, isSearching, trimmedQuery, groupedById]);

  const showPrices = bootstrap.behavior?.showPrices ?? true;

  if (groups.length === 0) {
    return (
      <div class="sbw-scroll">
        <div class="sbw-empty">No services available.</div>
      </div>
    );
  }

  return (
    <div class="sbw-scroll sbw-tiered">
      {/* Search input — sticky above the tabs. Typing here flattens the
          active-tab restriction so matches surface from every group. */}
      <div class="sbw-tier-search">
        <span class="sbw-tier-search-icon" aria-hidden="true">
          <SearchIcon />
        </span>
        <input
          type="search"
          placeholder="Search services"
          value={query}
          onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
          aria-label="Search services"
        />
        {isSearching && (
          <button
            type="button"
            class="sbw-tier-search-clear"
            onClick={() => setQuery('')}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {/* Category tabs — visually muted while searching since the search
          ignores them. Click still switches active category, which becomes
          relevant again the moment the operator clears the search. */}
      <div
        class={'sbw-tier-tabs' + (isSearching ? ' is-muted' : '')}
        role="tablist"
      >
        {groups.map((g) => {
          const isActive = g.id === activeKey;
          // Count base services (after grouping by name) — matches the
          // screenshot's "Color 9" style, not raw variant counts.
          const baseCount = new Set(
            g.services.map((s) =>
              s.name.includes(' — ') ? s.name.split(' — ')[0] : s.name,
            ),
          ).size;
          return (
            <button
              key={g.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              class={'sbw-tier-tab' + (isActive ? ' is-active' : '')}
              onClick={() => setActiveKey(g.id)}
            >
              <span class="sbw-tier-tab-label">{g.name}</span>
              <span class="sbw-tier-tab-count">
                {baseCount} {baseCount === 1 ? 'service' : 'services'}
              </span>
            </button>
          );
        })}
      </div>

      {/* Service rows. When searching we show all matches across groups;
          empty results state surfaces a clear message + clear-search CTA. */}
      <div class="sbw-tier-list">
        {isSearching && visible.length === 0 && (
          <div class="sbw-empty">
            No services match &ldquo;{query.trim()}&rdquo;.{' '}
            <button
              type="button"
              class="sbw-tier-empty-clear"
              onClick={() => setQuery('')}
            >
              Clear search
            </button>
          </div>
        )}
        {visible.map((svc) => {
          // Stack the buttons under the info block when there are too many
          // tiers for the side-by-side layout to read cleanly. Hair-style
          // catalogs use 2–3 tiers (Short/Medium/Long) and look right
          // inline; 4+ tiers (e.g. waxing menu — Brow / Underarm / Half
          // Leg / Full Leg / Arms / Back-Chest) collide with the title
          // column and wrap into a wall of buttons that squashes the
          // service name. Stacking matches what the mobile breakpoint
          // already does.
          const isStacked = svc.variants.length >= 4;
          return (
          <article
            key={svc.baseName}
            class={
              'sbw-tier-row' +
              (svc.isMostBooked ? ' is-most-booked' : '') +
              (isStacked ? ' is-stacked' : '')
            }
          >
            <div class="sbw-tier-row-info">
              {svc.isMostBooked && (
                <span class="sbw-tier-pill">MOST BOOKED</span>
              )}
              <h3 class="sbw-tier-row-name">{svc.baseName}</h3>
              {svc.description && (
                <p class="sbw-tier-row-desc">{svc.description}</p>
              )}
            </div>
            <div
              class={
                'sbw-tier-row-buttons' +
                (svc.variants.length === 1 ? ' is-single' : '')
              }
            >
              {svc.variants.map((v) => {
                const isSelected = selectedId === v.id;
                return (
                  <button
                    key={v.id}
                    type="button"
                    class={
                      'sbw-tier-btn' + (isSelected ? ' is-selected' : '')
                    }
                    onClick={() => onSelect(v.id)}
                  >
                    {v.tierLabel && (
                      <span class="sbw-tier-btn-label">
                        {v.tierLabel.toUpperCase()}
                      </span>
                    )}
                    {showPrices && (
                      <span class="sbw-tier-btn-price">
                        ${Math.round(v.price.amountCents / 100)}
                        {startingAt ? '+' : ''}
                      </span>
                    )}
                    {formatDuration(v.durationMinutes) && (
                      <span class="sbw-tier-btn-dur">
                        {formatDuration(v.durationMinutes)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </article>
          );
        })}
      </div>
    </div>
  );
}
